import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createStudy,
  getStudiesByStudentId,
  updateStudy,
} from "../../services/StudyService";
import { ToastContainer } from "react-toastify";
import { HSTSToast } from "../../utils/hstsToast";
import countries from "../../utils/countries.json";

export const StudentStudy = () => {
  const [studies, setStudies] = useState([]);
  const { id } = useParams();
  const navigator = useNavigate()

  const [showModal, setShowModal] = useState(false);
  const handleCloseModal = () => setShowModal(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editStudyId, setEditStudyId] = useState(null);

  const [newStudy, setNewStudy] = useState({
    level: "",
    univeristyName: "",
    country: "",
    finishDate: "",
    startDate: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStudy((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) validateField(name, value);
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, newStudy[field]);
  };

  const validateField = (field, value) => { 
    const copy = { ...errors };

    switch (field) {
      case "level":
        copy.level = value ? "" : "Level is required.";
        break;
      case "univeristyName":
        copy.univeristyName = value ? "" : "University name is required.";
        break;
      case "country":
        copy.country = value ? "" : "Country is required.";
        break;
      case "finishDate":
        if (!value) {
          copy.finishDate = "Finish date is required.";
        } else if (new Date(value) < new Date()) {
          copy.finishDate = "Finish date cannot be in the past.";
        } else {
          copy.finishDate = "";
        }
        break;
      case "startDate":
        if (!value) {
          copy.startDate = "Start date is required.";
        } else {
          copy.startDate = "";
        }
        break;
      default:
        break;
    }

    setErrors(copy);
  };

  const getInputClass = (field, value) => {
    if (!touched[field]) return "form-control";
    if (errors[field]) return "form-control is-invalid";
    if (value) return "form-control is-valid";
    return "form-control";
  };

  useEffect(() => {
    const fetchStudies = async () => {
      try {
        const response = await getStudiesByStudentId(id);
        setStudies(response.data);
      } catch (error) {
        console.error("Failed to fetch studies", error);
      }
    };

    fetchStudies();
  }, [id]);

  const handleEditClick = (study) => {
    setIsEditing(true);
    setEditStudyId(study.studyId);
    setNewStudy({
      level: study.level,
      univeristyName: study.univeristyName,
      country: study.country,
      startDate: study.startDate.slice(0, 10),
      finishDate: study.finishDate.slice(0, 10),
    });
    setTouched({});
    setErrors({});
    setShowModal(true);
  };

  const handleShowModal = () => {
    setIsEditing(false);
    setEditStudyId(null);
    setNewStudy({
      level: "",
      univeristyName: "",
      country: "",
      startDate: "",
      finishDate: "",
    });
    setTouched({});
    setErrors({});
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allFields = [
      "level",
      "univeristyName",
      "country",
      "startDate",
      "finishDate",
    ];
    setTouched((prev) => {
      const updated = {};
      allFields.forEach((field) => (updated[field] = true));
      return updated;
    });

    let valid = true;
    allFields.forEach((field) => {
      validateField(field, newStudy[field]);
      if (
        !newStudy[field] ||
        (field === "finishDate" && new Date(newStudy[field]) < new Date())
      ) {
        valid = false;
      }
    });

    if (!valid) {
      HSTSToast("error", "Please fill empty field.");
      return;
    }

    try {
      const studyData = { ...newStudy, studentId: { studentId: id } };

      if (isEditing) {
        await updateStudy(editStudyId, studyData);
        HSTSToast("success", "Study changed successfully");
      } else {
        await createStudy(studyData);
        HSTSToast("success", "Study added successfully");
      }

      const response = await getStudiesByStudentId(id);
      setStudies(response.data);

      handleCloseModal();
    } catch (error) {
      console.error("Failed to save study", error);
      HSTSToast("error", "Failed to save study. Try again");
    }
  };

  const getYearsOfStudy = (startDateStr, finishDateStr) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(finishDateStr);

    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    let result = `${years} year${years !== 1 ? "s" : ""}`;
    if (months > 0) {
      result += `, ${months} month${months !== 1 ? "s" : ""}`;
    }

    return result;
  };

  const viewMore = (id) => navigator(`/coordinator/viewProgress/${id}`);


  return (
    <>
      <ToastContainer />
      <div className="card-body">
        <div className="d-flex mb-4">
          <h5 className="mb-3">📚 Study details</h5>
          <button
            className="ms-auto btn btn-black rounded-5 py-0 me-2"
            onClick={handleShowModal}
          >
            <i className="fa fa-plus-circle me-1"></i> Add study
          </button>
        </div>
        {studies.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Level</th>
                  <th>University</th>
                  <th>Country</th>
                  <th>Years of Study</th>
                  <th>Satrt Date</th>
                  <th>Finish Date</th>
                  <th>Edit</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {studies.map((study, index) => (
                  <tr key={study.studyId}>
                    <td>{index + 1}</td>
                    <td>{study.level}</td>
                    <td>{study.univeristyName}</td>
                    <td>{study.country}</td>
                    <td>{getYearsOfStudy(new Date(study.startDate).toLocaleDateString(), new Date(study.finishDate).toLocaleDateString())}</td>
                    <td>{new Date(study.startDate).toLocaleDateString()}</td>
                    <td>{new Date(study.finishDate).toLocaleDateString()}</td>
                    <td
                      className="text-center zis-ponter"
                      title="Edit study informations"
                      onClick={() => handleEditClick(study)}
                    >
                       <span className="btn btn-secondary rounded-3 fw-bold">Edit </span>
                    </td>
                    <td
                      className="text-center zis-ponter"
                      title="View study progress"
                      onClick={() => viewMore(study.studyId)}
                    >
                       <span className="btn btn-primary rounded-3 fw-bold">View </span>
                      
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No studies found for this student.</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block mt-5" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content zis-shadow">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    <span
                      className={`fa ${
                        isEditing ? "fa-edit" : "fa-plus-circle"
                      } text-secondary me-1`}
                    ></span>
                    {isEditing ? "Change study" : "Add study"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body px-4">
                  <div className="mb-3">
                    <label className="form-label">Level</label>
                    <select
                      name="level"
                      value={newStudy.level}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur("level")}
                      className={getInputClass("level", newStudy.level)}
                    >
                      <option value="">Select Level</option>
                      {/* <option value="Bachelor">Bachelor Degree</option> */}
                      <option value="Master">Master Degree</option>
                      <option value="PhD">PhD</option>
                    </select>
                    {errors.level && (
                      <div className="invalid-feedback">{errors.level}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">University</label>
                    <input
                      type="text"
                      name="univeristyName"
                      value={newStudy.univeristyName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur("univeristyName")}
                      className={getInputClass(
                        "univeristyName",
                        newStudy.univeristyName
                      )}
                      placeholder="Enter university name"
                    />
                    {errors.univeristyName && (
                      <div className="invalid-feedback">
                        {errors.univeristyName}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Country</label>
                    <select
                      name="country"
                      value={newStudy.country}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur("country")}
                      className={getInputClass("country", newStudy.country)}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    {errors.country && (
                      <div className="invalid-feedback">{errors.country}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={newStudy.startDate}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur("startDate")}
                      className={getInputClass("startDate", newStudy.startDate)}
                      // min={new Date().toISOString().split("T")[0]}
                    />
                    {errors.startDate && (
                      <div className="invalid-feedback">{errors.startDate}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Finish Date</label>
                    <input
                      type="date"
                      name="finishDate"
                      value={newStudy.finishDate}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur("finishDate")}
                      className={getInputClass(
                        "finishDate",
                        newStudy.finishDate
                      )}
                      min={new Date().toISOString().split("T")[0]}
                    />
                    {errors.finishDate && (
                      <div className="invalid-feedback">
                        {errors.finishDate}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-danger rounded-2"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary rounded-2">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
